import type { EtapaDoFunil, Funil, ItemDoFunilNoCrm, Operacao } from "@base/contracts";
import { Dica, cn } from "@base/ui";
import { LEGENDA_DO_FUNIL, explicacaoDaEtapa } from "../domain/explicacoes";
import { formatar } from "../domain/formatadores";
import {
  calcularEtapas,
  conversaoCrmParaVenda,
  conversoesEntreEtapas,
  custosPorEtapa,
  largurasDoFunil,
  metaDoFunil,
  type Conversao,
  type CustoDaEtapa,
  type EtapaCalculada,
  type MetaDoFunil,
} from "../domain/funil";
import type { MetasDoPeriodo } from "../domain/indicadores";
import { PainelLateralDoFunil, TempoDeResposta } from "./painel-lateral-do-funil";

/** Barra da etapa: clara, com o numero em verde; a Venda em laranja, o resultado. */
const COR_DA_BARRA: Readonly<Record<EtapaDoFunil, string>> = {
  gerenciador: "bg-ice text-ink",
  crm: "bg-ice text-ink",
  mql: "bg-ice text-ink",
  agendado: "bg-ice text-ink",
  consulta: "bg-ice text-ink",
  venda: "bg-primary text-primary-foreground",
};

/** Area horizontal da barra clara; o bloco escuro da taxa vai ate a ponta. */
const AREA_DAS_BARRAS = 0.82;

type Linha = {
  readonly etapa: EtapaCalculada;
  readonly custo?: CustoDaEtapa;
  readonly saida?: Conversao;
  readonly largura: number;
};

function montarLinhas(
  etapas: readonly EtapaCalculada[],
  conversoes: readonly Conversao[],
  custos: ReadonlyMap<EtapaDoFunil, CustoDaEtapa>,
): readonly Linha[] {
  const larguras = largurasDoFunil(etapas);
  return etapas.map((etapa, indice) => {
    const custo = custos.get(etapa.etapa);
    const saida = conversoes[indice];
    return {
      etapa,
      largura: larguras[indice] ?? 1,
      ...(custo ? { custo } : {}),
      ...(saida ? { saida } : {}),
    };
  });
}

/**
 * Funil em escada: cada etapa e uma barra horizontal com a quantidade, e por
 * tras dela, um pouco mais abaixo, sai o bloco escuro com a taxa para a etapa
 * seguinte (RF-09). Na Venda, o bloco de tras mostra a taxa global CRM → Venda.
 * A esquerda, o nome, a descricao e o custo de cada etapa (RF-10). Ao lado do
 * funil, o painel com CPA, taxa global e os indicadores operacionais.
 */
export function FunilDeConversao({
  funil,
  investimentoCentavos,
  operacao,
  metas = null,
  etapaAtiva = null,
  aoEscolherEtapa,
}: {
  readonly funil: Funil;
  readonly investimentoCentavos: number | null;
  readonly operacao: Operacao;
  /** Metas do Funil Projetado para o periodo; `null` sem funil cadastrado. */
  readonly metas?: MetasDoPeriodo | null;
  readonly etapaAtiva?: ItemDoFunilNoCrm | null;
  readonly aoEscolherEtapa?: (etapa: ItemDoFunilNoCrm) => void;
}) {
  const etapas = calcularEtapas(funil);
  const conversoes = conversoesEntreEtapas(funil);
  const custos = new Map(custosPorEtapa(funil, investimentoCentavos).map((custo) => [custo.etapa, custo]));
  const global = conversaoCrmParaVenda(funil);
  const cpa = custos.get("venda");
  const linhas = montarLinhas(etapas, conversoes, custos);
  const meta = metaDoFunil(
    metas?.funil ? { funil: metas.funil, investimentoCentavos: metas.investimentoCentavos ?? null } : null,
  );

  return (
    <section aria-labelledby="titulo-funil" className="flex flex-col gap-bloco">
      <div className="flex flex-col gap-1">
        <h2 id="titulo-funil" className="flex items-center gap-2 text-xl font-normal tracking-tight">
          Funil de conversão
          <Dica rotulo="Como ler o funil" tipo="info">
            {LEGENDA_DO_FUNIL}
          </Dica>
        </h2>
        <p className="text-sm text-muted-foreground">
          Quantos chegaram a cada etapa e, atrás de cada barra, quanto seguiu para a próxima.
        </p>
      </div>

      <div className="grid items-stretch gap-bloco xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex h-full flex-col gap-bloco">
          <div className="rounded-3xl bg-card p-6 shadow-sm ring-1 ring-border/70 md:p-8">
            <ol aria-label="Etapas do funil" className="flex flex-col gap-7 pb-5">
              {linhas.map((linha) => (
                <LinhaDoFunil
                  key={linha.etapa.etapa}
                  linha={linha}
                  investimentoCentavos={investimentoCentavos}
                  global={linha.etapa.etapa === "venda" ? global : undefined}
                  meta={meta}
                  ativa={etapaAtiva === linha.etapa.etapa}
                  aoEscolher={
                    aoEscolherEtapa && linha.etapa.etapa !== "gerenciador"
                      ? () => aoEscolherEtapa(linha.etapa.etapa as ItemDoFunilNoCrm)
                      : undefined
                  }
                />
              ))}
            </ol>
          </div>
          <div className="mt-auto">
            <TempoDeResposta operacao={operacao} />
          </div>
        </div>

        <PainelLateralDoFunil
          cpa={cpa}
          global={global}
          operacao={operacao}
          metaCpa={meta?.custos.get("venda") ?? null}
          metaGlobal={meta?.global ?? null}
          leadsNoCrm={funil.crm}
          vendas={funil.venda}
        />
      </div>
    </section>
  );
}

function LinhaDoFunil({
  linha,
  investimentoCentavos,
  global,
  meta,
  ativa,
  aoEscolher,
}: {
  readonly linha: Linha;
  readonly investimentoCentavos: number | null;
  readonly global: Conversao | undefined;
  readonly meta: MetaDoFunil | null;
  readonly ativa: boolean;
  readonly aoEscolher?: (() => void) | undefined;
}) {
  const { etapa, saida, largura } = linha;
  const blocoDeTras = saida ?? global;
  const ehGlobal = !saida && global !== undefined;
  // Taxa de lead nao tem meta propria: a meta do CRM e a do gerenciador (100%).
  const metaDaTaxa = ehGlobal
    ? (meta?.global ?? null)
    : saida && saida.para !== "crm"
      ? (meta?.conversoes.get(saida.para) ?? null)
      : null;
  const escala = (fracao: number) => `${fracao * AREA_DAS_BARRAS * 100}%`;
  const metaDaEtapa = meta?.etapas.get(etapa.etapa) ?? null;

  return (
    <li className="grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-[12.5rem_minmax(0,1fr)] md:items-center">
      <div className="flex items-center justify-between gap-3 md:flex-col md:items-start md:gap-1.5">
        <div className="flex flex-col">
          <span className="flex items-center gap-1.5 text-sm font-semibold">
            {aoEscolher ? (
              <button
                type="button"
                onClick={aoEscolher}
                className={cn("text-left hover:underline", ativa && "text-primary")}
              >
                {etapa.rotulo}
              </button>
            ) : (
              etapa.rotulo
            )}
            <Dica rotulo={`Como ${etapa.rotulo} é calculado`}>
              <span className="flex flex-col gap-1.5">
                {explicacaoDaEtapa(etapa.etapa, saida?.para ?? null).map((linha) => (
                  <span key={linha}>{linha}</span>
                ))}
              </span>
            </Dica>
          </span>
          <span className="hidden text-xs leading-snug text-muted-foreground md:block">{etapa.descricao}</span>
        </div>
        <Custo linha={linha} investimentoCentavos={investimentoCentavos} metaDoCusto={meta?.custos.get(etapa.etapa) ?? null} />
      </div>

      <div className="relative h-14" title={`${etapa.rotulo}: ${etapa.quantidade === null ? "fonte não conectada" : formatar.quantidade(etapa.quantidade)}`}>
        {blocoDeTras ? (
          <div
            className={cn(
              "absolute top-4 right-0 bottom-[-1rem] flex items-end justify-end rounded-2xl px-4 pb-2",
              ehGlobal ? "bg-royal text-white" : "bg-secondary text-secondary-foreground",
            )}
            style={{ left: "1.25rem" }}
            title={`${blocoDeTras.nome} (${blocoDeTras.rotulo}): ${blocoDeTras.formula}`}
          >
            <span className="flex flex-col items-end leading-tight">
              <span className="text-[10px] font-medium opacity-80">{blocoDeTras.nome}</span>
              {metaDaTaxa !== null ? (
                <MetaEReal
                  meta={formatar.porcentagem(metaDaTaxa)}
                  real={formatar.porcentagem(blocoDeTras.taxa)}
                  tamanho="medio"
                />
              ) : (
                <span className="text-lg font-semibold tracking-tight tabular-nums">
                  {formatar.porcentagem(blocoDeTras.taxa)}
                </span>
              )}
            </span>
          </div>
        ) : null}

        <div
          className={cn(
            "absolute inset-y-0 left-0 z-10 flex items-center rounded-2xl px-4 sm:px-5 transition-all duration-700 ease-out",
            etapa.quantidade === null
              ? "border-2 border-dashed border-border bg-muted text-muted-foreground"
              : cn("shadow-lg", COR_DA_BARRA[etapa.etapa]),
          )}
          style={{ width: escala(largura) }}
        >
          {metaDaEtapa !== null ? (
            <MetaEReal
              meta={formatar.quantidade(Math.round(metaDaEtapa))}
              real={etapa.quantidade === null ? "—" : formatar.quantidade(etapa.quantidade)}
              tamanho="grande"
            />
          ) : etapa.quantidade === null ? (
            <span className="text-sm font-medium">Conecte Meta ou Google</span>
          ) : (
            <span className="text-2xl font-semibold tracking-[-0.03em] tabular-nums">
              {formatar.quantidade(etapa.quantidade)}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}

function Custo({
  linha,
  investimentoCentavos,
  metaDoCusto,
}: {
  readonly linha: Linha;
  readonly investimentoCentavos: number | null;
  readonly metaDoCusto: number | null;
}) {
  const { etapa, custo } = linha;
  const ehCpa = etapa.etapa === "venda";
  const sigla = etapa.etapa === "gerenciador" ? "Investimento" : (custo?.sigla ?? "");
  const valor =
    etapa.etapa === "gerenciador"
      ? investimentoCentavos === null
        ? null
        : investimentoCentavos / 100
      : (custo?.valor ?? null);

  return (
    <span
      className={cn(
        "inline-flex items-baseline gap-1.5 rounded-full px-2.5 py-1 text-xs whitespace-nowrap",
        ehCpa ? "bg-primary font-semibold text-primary-foreground" : "bg-muted text-muted-foreground",
      )}
      title={custo?.nome}
    >
      {sigla}
      {/* Com meta: "Meta | Real", no mesmo padrao das barras. */}
      {metaDoCusto !== null ? (
        <span className={cn("tabular-nums", ehCpa ? "opacity-85" : "text-muted-foreground")}>
          {formatar.moeda(metaDoCusto)}
          <span className="px-1 opacity-50">|</span>
          <strong className={cn("font-semibold", !ehCpa && "text-foreground")}>
            {valor === null ? "—" : formatar.moeda(valor)}
          </strong>
        </span>
      ) : (
        <strong className={cn("font-semibold tabular-nums", !ehCpa && "text-foreground")}>
          {valor === null ? "—" : formatar.moeda(valor)}
        </strong>
      )}
    </span>
  );
}

/**
 * "Meta | Real" so com numeros: a meta menor e clara, um filete separando e o
 * realizado em destaque. A legenda fica no (i) do titulo do funil.
 */
function MetaEReal({
  meta,
  real,
  tamanho,
}: {
  readonly meta: string;
  readonly real: string;
  readonly tamanho: "grande" | "medio";
}) {
  const grande = tamanho === "grande";
  return (
    <span className="flex items-center gap-2.5 tabular-nums" aria-label={`Meta ${meta}, realizado ${real}`}>
      <span className={cn("font-medium opacity-55", grande ? "text-base" : "text-sm")}>{meta}</span>
      <span aria-hidden="true" className={cn("w-px self-stretch bg-current opacity-25", grande ? "my-1" : "my-0.5")} />
      <span className={cn("font-semibold tracking-[-0.03em]", grande ? "text-2xl" : "text-lg")}>{real}</span>
    </span>
  );
}
